import type { SettingsRoute } from '../settingsPageComponents'
import type { ConfigArea } from '@omnea-app/authz'

/**
 * The access scope of a settings route: the config area governing it, 'personal' for per-user
 * pages that are never config-gated, or 'permission-management' for the permissions pages,
 * which follow the can_manage_permissions relation rather than any config area.
 */
export type SettingsAccessScope = ConfigArea | 'personal' | 'permission-management'

/**
 * Maps every settings route to its config area. Exhaustive over SettingsRoute (plus the
 * separately-routed integrations and environment-sync pages) so a new settings page cannot ship
 * without declaring which area governs it. Omnean-only pages keep their isOmnean gate on top of this.
 */
export const CONFIG_AREA_BY_SETTINGS_ROUTE: Record<SettingsRoute | 'integrations' | 'environment-sync', SettingsAccessScope> = {
  notifications: 'personal',
  preferences: 'personal',

  workflows: 'workflows_and_forms',
  forms: 'workflows_and_forms',
  'environment-sync': 'workflows_and_forms',

  'dynamic-assignees': 'dynamic_assignees',

  'users-and-groups': 'users_and_groups',

  permissions: 'permission-management',

  'core-data': 'core_data',

  'document-storage': 'document_review',

  integrations: 'it',
  security: 'it',
  'user-properties': 'it',
  api: 'it',

  admin: 'organisation_settings',
  'agreements-migration': 'organisation_settings',
  'agreements-migration-configure-mappings': 'organisation_settings',
  'agreements-migration-migrate-engagements': 'organisation_settings',
  'agreements-migration-migrate-in-flight': 'organisation_settings',
  'agreements-migration-migrate-renewals': 'organisation_settings',
  'agreements-migration-verify-in-flight': 'organisation_settings',
  ai: 'organisation_settings',
  'ai-bulk-extraction': 'organisation_settings',
  auctions: 'organisation_settings',
  'audit-log': 'organisation_settings',
  'auto-flagged-categories': 'organisation_settings',
  catalogues: 'organisation_settings',
  categories: 'organisation_settings',
  'default-notifications': 'organisation_settings',
  'external-archive': 'organisation_settings',
  '__feature-flags': 'organisation_settings',
  'goods-receipting': 'organisation_settings',
  'hosted-catalogues': 'organisation_settings',
  import: 'organisation_settings',
  localisation: 'organisation_settings',
  policies: 'organisation_settings',
  'product-catalogue': 'organisation_settings',
  'punchout-catalogues': 'organisation_settings',
  risks: 'organisation_settings',
  'risk-alerts': 'organisation_settings',
  'subprocessor-settings': 'organisation_settings',
  'risk-management': 'organisation_settings',
  sourcing: 'organisation_settings',
} as const

export const getSettingsRouteAccessScope = (route: string | undefined): SettingsAccessScope | undefined => {
  // Sub-paths (e.g. integrations/<orgIntegrationId>) inherit the scope of their base route.
  const baseRoute = route?.split('/')[0]
  return baseRoute != null && baseRoute in CONFIG_AREA_BY_SETTINGS_ROUTE
    ? CONFIG_AREA_BY_SETTINGS_ROUTE[baseRoute as keyof typeof CONFIG_AREA_BY_SETTINGS_ROUTE]
    : undefined
}
